var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState, useCallback, useEffect } from 'react';
// FaComments / FaBoxOpen / FaFlag dropped with the hidden Dialogues/Items/Missions tabs.
import { FaMap, FaBolt, FaCloudUploadAlt, FaWindowMaximize } from 'react-icons/fa';
import MapArtGrid from '../MapArtGrid/MapArtGrid';
import DialoguesArt from '../DialoguesArt/DialoguesArt';
import EventsArt from '../EventsArt/EventsArt';
import ItemsArt from '../ItemsArt/ItemsArt';
import UxPanelsArt from '../UxPanelsArt/UxPanelsArt';
import MissionsArt from '../MissionsArt/MissionsArt';
import { BulkUploadModal } from '../BulkUploadModal/BulkUploadModal';
import { DialoguesBulkUploadModal } from '../DialoguesArt/DialoguesBulkUploadModal';
import { EventsBulkUploadModal } from '../EventsArt/EventsBulkUploadModal';
import { ItemsBulkUploadModal } from '../ItemsArt/ItemsBulkUploadModal';
import { UxPanelsBulkUploadModal } from '../UxPanelsArt/UxPanelsBulkUploadModal';
import { MissionsBulkUploadModal } from '../MissionsArt/MissionsBulkUploadModal';
import './ArtManager.scss';
import { getNodeImages } from '../../api/images';
import { getDialogueImages } from '../../api/dialogue-images';
export const ArtManager = ({ levelId, levelWidth, levelHeight, nodes, onNodeImageChange }) => {
    const [activeSection, setActiveSection] = useState('map');
    // First-visit hint: how to name + bulk-upload cell art (dismissed permanently).
    const [showArtHint, setShowArtHint] = useState(() => {
        try {
            return localStorage.getItem('vm-art-hint-seen') !== '1';
        }
        catch (_a) {
            return true;
        }
    });
    const dismissArtHint = useCallback(() => {
        setShowArtHint(false);
        try {
            localStorage.setItem('vm-art-hint-seen', '1');
        }
        catch ( /* ignore */_a) { /* ignore */ }
    }, []);
    const [uploadedCount, setUploadedCount] = useState(0);
    const [dialogueUploadedCount, setDialogueUploadedCount] = useState(0);
    const [dialogueUsedCount, setDialogueUsedCount] = useState(0);
    const [eventUploadedCount, setEventUploadedCount] = useState(0);
    const [eventUsedCount, setEventUsedCount] = useState(0);
    const [itemUploadedCount, setItemUploadedCount] = useState(0);
    const [itemUsedCount, setItemUsedCount] = useState(0);
    const [uxPanelUploadedCount, setUxPanelUploadedCount] = useState(0);
    const [uxPanelUsedCount, setUxPanelUsedCount] = useState(0);
    const [missionUploadedCount, setMissionUploadedCount] = useState(0);
    const [missionUsedCount, setMissionUsedCount] = useState(0);
    const [showBulk, setShowBulk] = useState(false);
    const [showBulkDialogues, setShowBulkDialogues] = useState(false);
    const [showBulkEvents, setShowBulkEvents] = useState(false);
    const [showBulkItems, setShowBulkItems] = useState(false);
    const [showBulkUxPanels, setShowBulkUxPanels] = useState(false);
    const [showBulkMissions, setShowBulkMissions] = useState(false);
    const [refreshTrigger, setRefreshTrigger] = useState(0);
    const [dialogueRefreshTrigger, setDialogueRefreshTrigger] = useState(0);
    const [occupied, setOccupied] = useState(new Set());
    // refresh function
    const refreshCount = useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        const images = yield getNodeImages(levelId);
        setUploadedCount(images.length);
    }), [levelId]);
    useEffect(() => { refreshCount(); }, [refreshCount]);
    const handleImagesUpdated = (count) => {
        setUploadedCount(count);
    };
    const handleSectionChange = (section) => {
        setActiveSection(section);
    };
    const renderContent = () => {
        if (activeSection === 'map') {
            return _jsxs(_Fragment, { children: [showArtHint && (_jsxs("div", { className: "art-hint", children: [_jsxs("div", { className: "art-hint-body", children: [_jsx(FaCloudUploadAlt, { className: "art-hint-icon" }), _jsxs("span", { children: [_jsx("b", { children: "Tip:" }), " name each image file after its cell \u2014 ", _jsx("code", { children: "A1.png" }), ", ", _jsx("code", { children: "B3.jpg" }), ", \u2026 \u2014 then use ", _jsx("b", { children: "Bulk Upload" }), " and they drop straight into the matching cells. You can also click a single cell to set its art."] })] }), _jsx("button", { type: "button", className: "art-hint-close", onClick: dismissArtHint, title: "Got it", children: "\u00D7" })] })), _jsx(MapArtGrid, { levelId: levelId, mapWidth: levelWidth, mapHeight: levelHeight, onImagesUpdated: handleImagesUpdated, refreshTrigger: refreshTrigger, onImageChange: onNodeImageChange })] });
        }
        if (activeSection === 'dialogues') {
            return _jsx(DialoguesArt, { levelId: levelId, refreshTrigger: dialogueRefreshTrigger });
        }
        if (activeSection === 'events') {
            return _jsx(EventsArt, { levelId: levelId, refreshTrigger: refreshTrigger, nodes: nodes });
        }
        if (activeSection === 'items') {
            return _jsx(ItemsArt, { levelId: levelId, refreshTrigger: refreshTrigger });
        }
        if (activeSection === 'missions') {
            return _jsx(MissionsArt, { levelId: levelId, refreshTrigger: refreshTrigger });
        }
        // uxpanels section
        return _jsx(UxPanelsArt, { levelId: levelId, refreshTrigger: refreshTrigger, nodes: nodes });
    };
    // When bulk modal toggles on, load occupied set
    useEffect(() => {
        if (showBulk) {
            (() => __awaiter(void 0, void 0, void 0, function* () {
                const imgs = yield getNodeImages(levelId);
                const set = new Set();
                imgs.forEach(i => set.add(i.coord));
                setOccupied(set);
            }))();
        }
    }, [showBulk, levelId]);
    // Effect to refresh dialogue count when section or trigger changes
    useEffect(() => {
        if (activeSection === 'dialogues') {
            (() => __awaiter(void 0, void 0, void 0, function* () {
                try {
                    const [imgs, dlgs] = yield Promise.all([
                        getDialogueImages(levelId),
                        (yield import('../../api/dialogues')).getLevelDialogues(levelId)
                    ]);
                    setDialogueUploadedCount(imgs.length);
                    const used = imgs.filter(img => dlgs.some((d) => d.backgroundImageId === img.id)).length;
                    setDialogueUsedCount(used);
                }
                catch (err) {
                    console.error('Failed to load dialogue images or dialogues', err);
                }
            }))();
        }
    }, [activeSection, levelId, showBulkDialogues, dialogueRefreshTrigger]);
    // Effect for Event images counts
    useEffect(() => {
        if (activeSection === 'events') {
            (() => __awaiter(void 0, void 0, void 0, function* () {
                try {
                    const [imgs, evts] = yield Promise.all([
                        (yield import('../../api/event-images')).getEventImages(levelId),
                        (yield import('../../api/level-events')).getLevelEvents(levelId)
                    ]);
                    setEventUploadedCount(imgs.length);
                    const used = imgs.filter(img => evts.some((e) => e.backgroundImage === img.imageUrl)).length;
                    setEventUsedCount(used);
                }
                catch (err) {
                    console.error('Failed to load event images/events', err);
                }
            }))();
        }
    }, [activeSection, levelId, refreshTrigger]);
    // Effect for Item images counts (based on Items)
    useEffect(() => {
        if (activeSection === 'items') {
            (() => __awaiter(void 0, void 0, void 0, function* () {
                try {
                    const [imgs, items] = yield Promise.all([
                        (yield import('../../api/item-images')).getItemImages(levelId),
                        (yield import('../../api/items')).getLevelItems(levelId)
                    ]);
                    setItemUploadedCount(imgs.length);
                    const used = imgs.filter(img => items.some((it) => it.imageId === img.id)).length;
                    setItemUsedCount(used);
                }
                catch (err) {
                    console.error('Failed to load item images/items', err);
                }
            }))();
        }
    }, [activeSection, levelId, refreshTrigger]);
    // Effect for UX Panel images counts
    useEffect(() => {
        if (activeSection === 'uxpanels') {
            (() => __awaiter(void 0, void 0, void 0, function* () {
                try {
                    const imgs = yield (yield import('../../api/ux-panel-images')).getUxPanelImages(levelId);
                    setUxPanelUploadedCount(imgs.length);
                    // While UX panels are not implemented, treat usedCount as 0
                    setUxPanelUsedCount(0);
                }
                catch (err) {
                    console.error('Failed to load ux panel images', err);
                }
            }))();
        }
    }, [activeSection, levelId, refreshTrigger]);
    // Effect for Mission images counts
    useEffect(() => {
        if (activeSection === 'missions') {
            (() => __awaiter(void 0, void 0, void 0, function* () {
                try {
                    const imgs = yield (yield import('../../api/mission-images')).getMissionImages(levelId);
                    setMissionUploadedCount(imgs.length);
                    setMissionUsedCount(0); // Missions are not yet implemented, so usedCount is 0
                }
                catch (err) {
                    console.error('Failed to load mission images', err);
                }
            }))();
        }
    }, [activeSection, levelId, refreshTrigger]);
    return (_jsxs("div", { className: "art-manager", children: [_jsxs("div", { className: "art-manager-header", children: [_jsxs("div", { className: "art-navigation", children: [_jsxs("button", { className: `nav-button ${activeSection === 'map' ? 'active' : ''}`, onClick: () => handleSectionChange('map'), title: "Map Art Grid", children: [_jsx(FaMap, {}), " Map"] }), _jsxs("button", { className: `nav-button ${activeSection === 'events' ? 'active' : ''}`, onClick: () => handleSectionChange('events'), title: "Events Art Grid", children: [_jsx(FaBolt, {}), " Events"] }), _jsxs("button", { className: `nav-button ${activeSection === 'uxpanels' ? 'active' : ''}`, onClick: () => handleSectionChange('uxpanels'), title: "UX Panels Art", children: [_jsx(FaWindowMaximize, {}), " UX Panels"] })] }), _jsxs("div", { className: "art-info-section", children: [activeSection === 'map' && (_jsxs("button", { className: "nav-button bulk-button", onClick: () => setShowBulk(true), title: "Bulk Upload Images", children: [_jsx(FaCloudUploadAlt, {}), " Bulk Upload"] })), activeSection === 'dialogues' && (_jsxs("button", { className: "nav-button bulk-button", onClick: () => setShowBulkDialogues(true), title: "Bulk Upload Backgrounds", children: [_jsx(FaCloudUploadAlt, {}), " Bulk Upload"] })), activeSection === 'events' && (_jsxs("button", { className: "nav-button bulk-button", onClick: () => setShowBulkEvents(true), title: "Bulk Upload Event Backgrounds", children: [_jsx(FaCloudUploadAlt, {}), " Bulk Upload"] })), activeSection === 'items' && (_jsxs("button", { className: "nav-button bulk-button", onClick: () => setShowBulkItems(true), title: "Bulk Upload Item Backgrounds", children: [_jsx(FaCloudUploadAlt, {}), " Bulk Upload"] })), activeSection === 'uxpanels' && (_jsxs("button", { className: "nav-button bulk-button", onClick: () => setShowBulkUxPanels(true), title: "Bulk Upload UX Panel Images", children: [_jsx(FaCloudUploadAlt, {}), " Bulk Upload"] })), activeSection === 'missions' && (_jsxs("button", { className: "nav-button bulk-button", onClick: () => setShowBulkMissions(true), title: "Bulk Upload Mission Images", children: [_jsx(FaCloudUploadAlt, {}), " Bulk Upload"] })), _jsxs("div", { className: "art-info-panel", children: [activeSection === 'map' && (_jsxs(_Fragment, { children: [_jsxs("span", { className: "info-item", children: ["Grid: ", levelWidth, "\u00D7", levelHeight] }), _jsxs("span", { className: "info-item", children: ["Uploaded: ", _jsxs("span", { className: "upload-progress", children: [uploadedCount, "/", levelWidth * levelHeight] })] })] })), activeSection === 'dialogues' && (_jsxs("span", { className: "info-item", children: ["Dialogue Backgrounds Used: ", _jsxs("span", { className: "upload-progress", children: [dialogueUsedCount, "/", dialogueUploadedCount] })] })), activeSection === 'events' && (_jsxs("span", { className: "info-item", children: ["Event Backgrounds Used: ", _jsxs("span", { className: "upload-progress", children: [eventUsedCount, "/", eventUploadedCount] })] })), activeSection === 'items' && (_jsxs("span", { className: "info-item", children: ["Item Images Used: ", _jsxs("span", { className: "upload-progress", children: [itemUsedCount, "/", itemUploadedCount] })] })), activeSection === 'uxpanels' && (_jsxs("span", { className: "info-item", children: ["UX Panel Images: ", _jsxs("span", { className: "upload-progress", children: [uxPanelUsedCount, "/", uxPanelUploadedCount] })] })), activeSection === 'missions' && (_jsxs("span", { className: "info-item", children: ["Mission Images: ", _jsxs("span", { className: "upload-progress", children: [missionUsedCount, "/", missionUploadedCount] })] }))] })] })] }), _jsxs("div", { className: "art-manager-content", children: [renderContent(), showBulk && (_jsx(BulkUploadModal, { isOpen: showBulk, onClose: () => setShowBulk(false), levelId: levelId, gridWidth: levelWidth, gridHeight: levelHeight, occupied: occupied, onUploaded: () => { refreshCount(); setRefreshTrigger(prev => prev + 1); } })), showBulkDialogues && (_jsx(DialoguesBulkUploadModal, { isOpen: showBulkDialogues, onClose: () => setShowBulkDialogues(false), levelId: levelId, onUploaded: () => { setShowBulkDialogues(false); setDialogueRefreshTrigger(prev => prev + 1); } })), showBulkEvents && (_jsx(EventsBulkUploadModal, { isOpen: showBulkEvents, onClose: () => setShowBulkEvents(false), levelId: levelId, onUploaded: () => { setShowBulkEvents(false); setRefreshTrigger(prev => prev + 1); } })), showBulkItems && (_jsx(ItemsBulkUploadModal, { isOpen: showBulkItems, onClose: () => setShowBulkItems(false), levelId: levelId, onUploaded: () => { setShowBulkItems(false); setRefreshTrigger(prev => prev + 1); } })), showBulkUxPanels && (_jsx(UxPanelsBulkUploadModal, { isOpen: showBulkUxPanels, onClose: () => setShowBulkUxPanels(false), levelId: levelId, onUploaded: () => { setShowBulkUxPanels(false); setRefreshTrigger(prev => prev + 1); } })), showBulkMissions && (_jsx(MissionsBulkUploadModal, { isOpen: showBulkMissions, onClose: () => setShowBulkMissions(false), levelId: levelId, onUploaded: () => { setShowBulkMissions(false); setRefreshTrigger(prev => prev + 1); } }))] })] }));
};
